import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { shuffle } from 'lodash';

import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { PhoenixIcon } from '@phoenix/design-system/frontend/ui/phoenix-icon/phoenix-icon';

import { PromotionalHeader, PromotionalImage } from '@phoenix/team-m/dashboard/frontend/ui/from-the-shop/compatible-products';
import { CompatibleBatteriesCatalog, RuntimeBarLegend } from '@phoenix/team-m/dashboard/frontend/ui/from-the-shop/compatible-batteries';
import { OverviewSubsection } from '@phoenix/team-m/dashboard/frontend/ui/overview/overview-subsection';

import { ProductCompatibleBatteries } from '@phoenix/team-m/dashboard/frontend/util/product/product-batteries.model';

export const CompatibleBatteries = ({ productCompatibleBatteries }: { productCompatibleBatteries: ProductCompatibleBatteries[] }) => {
  const { t } = useTranslation();

  const shuffledBatteries = useMemo(() => shuffle(productCompatibleBatteries), [JSON.stringify(productCompatibleBatteries)]);

  return productCompatibleBatteries.length ? (
    <OverviewSubsection dataTestId="compatibleBatteries" headline={t('dashboard_batteries_teaser_headline')}>
      <div className="mt-6 grid grid-cols-3 gap-6">
        <PromotionalHeader
          description={t('dashboard_fromTheShop_batteries_introduction_paragraph')}
          className="col-span-3 desktop:col-span-2"
        >
          <>
            <div className="mt-6 grid grid-cols-1 gap-4 desktop:grid-cols-2">
              <RuntimeBarLegend
                items={[
                  [t('dashboard_fromTheShop_batteries_legend_current'), 'blue-deep'],
                  [t('dashboard_fromTheShop_batteries_legend_runtimeIncrease'), 'blue'],
                ]}
              />
              <div className="grid grid-cols-[min-content_1fr] items-center gap-4 rounded-md bg-secondary-lighter p-4 text-secondary-dark stihl-text-body-sm-bold desktop:py-1">
                <PhoenixIcon className="mt-[2px] h-4 max-w-[16px] text-secondary-deep" icon={getDesignSystemIcon('icon-battery-16.svg')} />
                <span>{t('dashboard_fromTheShop_batteries_hint_addOwnedBatteries')}</span>
              </div>
            </div>
            <div className="flex gap-3 px-2 pt-6 stihl-text-body-sm-reg">
              <PhoenixIcon className="mt-[2px] h-4 max-w-[16px] text-secondary-deep" icon={getDesignSystemIcon('icon-info-circle.svg')} />
              {t('dashboard_fromTheShop_batteries_info_disclaimer')}
            </div>
          </>
        </PromotionalHeader>
        <PromotionalImage imageSrc="batteries-set.png" className="hidden desktop:block" />
      </div>
      <CompatibleBatteriesCatalog className="pt-6" batteries={shuffledBatteries} />
    </OverviewSubsection>
  ) : null;
};
